import React from 'react';
import { Box, Drawer, AppBar, Toolbar, Typography, List, ListItem, ListItemIcon, ListItemText, IconButton, Button, ListItemButton, Divider, Avatar, Menu, MenuItem, Badge } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PaymentsIcon from '@mui/icons-material/Payments';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import PercentIcon from '@mui/icons-material/Percent';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TimelineIcon from '@mui/icons-material/Timeline';
import EventNoteIcon from '@mui/icons-material/EventNote';
import SecurityIcon from '@mui/icons-material/Security';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import { Collapse } from '@mui/material';
import { Link } from 'react-router-dom';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import NotificationsIcon from '@mui/icons-material/Notifications';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const drawerWidth = 280;

interface LayoutProps {
  children: React.ReactNode;
}

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path?: string;
  type: 'item' | 'group';
  key?: string;
  children?: {
    text: string;
    path: string;
    icon: React.ReactNode;
  }[];
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [openMenus, setOpenMenus] = React.useState<{ [key: string]: boolean }>({
    parameters: false,
    transactions: false,
    reports: false,
    administration: false
  });
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const [notifAnchorEl, setNotifAnchorEl] = React.useState<null | HTMLElement>(null);
  const notifOpen = Boolean(notifAnchorEl);
  const [unreadCount, setUnreadCount] = React.useState(3);
  const sampleNotifications = [
    { type: 'success', message: 'Payment received for Loan #L120.' },
    { type: 'info', message: 'Loan #L123 disbursed to ABC Corp.' },
    { type: 'warning', message: '3 payments are due this week.' },
    { type: 'alert', message: 'System maintenance scheduled for Sunday.' }
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (menuKey: string) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleManageProfile = () => {
    navigate('/profile');
    handleMenuClose();
  };
  const handleChangePassword = () => {
    navigate('/change-password');
    handleMenuClose();
  };
  const handleLogoutMenu = () => {
    handleMenuClose();
    handleLogout();
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    navigate('/login');
  };

  const handleNotifClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotifAnchorEl(event.currentTarget);
    setUnreadCount(0); // Mark as read when opened
  };
  const handleNotifClose = () => {
    setNotifAnchorEl(null);
  };

  const getNotifIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircleOutlineIcon sx={{ color: '#10B981' }} fontSize="small" />;
      case 'info':
        return <InfoOutlinedIcon sx={{ color: '#6366F1' }} fontSize="small" />;
      case 'warning':
        return <WarningAmberOutlinedIcon sx={{ color: '#F59E0B' }} fontSize="small" />;
      case 'alert':
      case 'error':
        return <ErrorOutlineIcon sx={{ color: '#F43F5E' }} fontSize="small" />;
      default:
        return <InfoOutlinedIcon fontSize="small" />;
    }
  };

  const menuItems: MenuItem[] = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      type: 'item'
    },
    {
      text: 'Parameters',
      icon: <SettingsIcon />,
      type: 'item',
      path: '/parameters',
    },
    {
      text: 'Transactions',
      icon: <PaymentsIcon />,
      type: 'group',
      key: 'transactions',
      children: [
        { text: 'Loan Transactions', path: '/transactions/loan', icon: <ReceiptIcon /> },
        { text: 'Loan Utilization', path: '/transactions/loan-utilization', icon: <TimelineIcon /> },
        { text: 'Repayment Schedule', path: '/transactions/repayment', icon: <EventNoteIcon /> }
      ]
    },
    {
      text: 'Reports',
      icon: <AssessmentIcon />,
      type: 'item',
      path: '/reports',
    },
    {
      text: 'Administration',
      icon: <AdminPanelSettingsIcon />,
      type: 'group',
      key: 'administration',
      children: [
        { text: 'User Management', path: '/administration/user-management', icon: <ManageAccountsIcon /> },
        { text: 'Role Management', path: '/administration/role-management', icon: <SecurityIcon /> },
        { text: 'Audit Trail Viewer', path: '/administration/audit-trail-viewer', icon: <HistoryIcon /> },
        { text: 'System Configuration', path: '/administration/system-configuration', icon: <SettingsIcon /> }
      ]
    }
  ];

  const drawer = (
    <Box sx={{ height: '100%', bgcolor: '#FFFFFF' }}>
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center',
        gap: 1
      }}>
        <TrendingUpIcon sx={{ color: '#6366F1', fontSize: 32 }} />
        <Box>
          <Typography variant="h6" sx={{ 
            color: '#6366F1',
            fontWeight: 'bold',
            letterSpacing: '-0.5px',
            lineHeight: 1.2
          }}>
            MJS Holding
          </Typography>
          <Typography variant="caption" sx={{ 
            color: '#6366F1',
            fontWeight: 500,
            letterSpacing: '-0.25px'
          }}>
            Treasury Solutions
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ mb: 0 }} />
      <List sx={{ p: 2 }}>
        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
            {item.type === 'item' && item.path ? (
              <ListItemButton
                component={Link}
                to={item.path}
                selected={location.pathname === item.path}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  pl: 2.5,
                  py: 1.2,
                  color: location.pathname === item.path ? '#6366F1' : '#1E293B',
                  bgcolor: location.pathname === item.path ? '#EEF2FF' : 'transparent',
                  borderLeft: location.pathname === item.path ? '4px solid #6366F1' : '4px solid transparent',
                  fontWeight: location.pathname === item.path ? 700 : 500,
                  '&:hover': {
                    bgcolor: '#F3F4F6',
                    color: '#6366F1',
                  },
                  transition: 'all 0.18s',
                }}
              >
                <ListItemIcon sx={{ 
                  color: '#6366F1',
                  minWidth: 40
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: '#374151',
                      fontWeight: 500,
                      fontSize: '0.9375rem',
                    }
                  }}
                />
              </ListItemButton>
            ) : item.type === 'group' && item.key ? (
              <>
                <ListItemButton
                  onClick={() => handleMenuClick(item.key as string)}
                  selected={openMenus[item.key]}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    pl: 2.5,
                    py: 1.2,
                    color: openMenus[item.key] ? '#6366F1' : '#1E293B',
                    bgcolor: openMenus[item.key] ? '#EEF2FF' : 'transparent',
                    borderLeft: openMenus[item.key] ? '4px solid #6366F1' : '4px solid transparent',
                    fontWeight: openMenus[item.key] ? 700 : 500,
                    '&:hover': {
                      bgcolor: '#F3F4F6',
                      color: '#6366F1',
                    },
                    transition: 'all 0.18s',
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: '#6366F1',
                    minWidth: 40
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        color: '#374151',
                        fontWeight: 500,
                        fontSize: '0.9375rem',
                      }
                    }}
                  />
                  {openMenus[item.key] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={openMenus[item.key]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children && item.children.map((child) => (
                      <ListItemButton
                        component={Link}
                        to={child.path}
                        selected={location.pathname === child.path}
                        sx={{
                          pl: 7,
                          borderRadius: 1,
                          mb: 0.5,
                          color: location.pathname === child.path ? '#6366F1' : '#1E293B',
                          bgcolor: location.pathname === child.path ? '#EEF2FF' : 'transparent',
                          borderLeft: location.pathname === child.path ? '4px solid #6366F1' : '4px solid transparent',
                          fontWeight: location.pathname === child.path ? 700 : 500,
                          '&:hover': {
                            bgcolor: '#F3F4F6',
                            color: '#6366F1',
                          },
                          transition: 'all 0.18s',
                        }}
                      >
                        <ListItemIcon sx={{ 
                          color: '#6366F1',
                          minWidth: 40,
                          ml: -2
                        }}>
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={child.text}
                          sx={{
                            '& .MuiListItemText-primary': {
                              color: '#374151',
                              fontWeight: 400,
                              fontSize: '0.875rem',
                            }
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : null}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', bgcolor: '#F9FAFB', minHeight: '100vh', position: 'relative' }}>
      <Divider sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', zIndex: 1201, borderBottomWidth: 2, borderColor: '#E5E7EB' }} />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: '#fff',
          color: '#1E293B',
          boxShadow: '0 2px 8px 0 rgba(99,102,241,0.06)',
          borderBottom: '1px solid #E5E7EB',
          zIndex: 1201,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: '#374151' }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton color="inherit" onClick={handleNotifClick} sx={{ mr: 1, p: 0 }}>
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon sx={{ color: '#6366F1', fontSize: 36 }} />
            </Badge>
          </IconButton>
          <Menu
            anchorEl={notifAnchorEl}
            open={notifOpen}
            onClose={handleNotifClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { minWidth: 320 } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1 }}>
              <Typography
                variant="body2"
                sx={{ color: '#6366F1', fontWeight: 500, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => { setUnreadCount(0); handleNotifClose(); }}
              >
                Mark all as read
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#374151', fontWeight: 500, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => { handleNotifClose(); navigate('/notifications'); }}
              >
                View all
              </Typography>
            </Box>
            <Typography sx={{ px: 2, py: 1, fontWeight: 600, color: '#374151' }}>Notifications</Typography>
            <Divider />
            {sampleNotifications.length === 0 ? (
              <MenuItem disabled>No new notifications</MenuItem>
            ) : (
              sampleNotifications.map((notif, idx) => (
                <React.Fragment key={idx}>
                  <MenuItem sx={{ whiteSpace: 'normal', alignItems: 'flex-start', gap: 1 }}>
                    {getNotifIcon(notif.type)}
                    <span>{notif.message}</span>
                  </MenuItem>
                  {idx < sampleNotifications.length - 1 && <Divider sx={{ my: 0, mx: 2 }} />}
                </React.Fragment>
              ))
            )}
          </Menu>
          <IconButton onClick={handleAvatarClick} sx={{ ml: 1 }}>
            <Avatar sx={{ bgcolor: '#6366F1', width: 36, height: 36 }}>U</Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleManageProfile}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              Manage Profile
            </MenuItem>
            <MenuItem onClick={handleChangePassword}>
              <ListItemIcon>
                <TuneIcon fontSize="small" />
              </ListItemIcon>
              Change Password
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogoutMenu}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: '1px solid #E5E7EB',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: '1px solid #E5E7EB',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          bgcolor: '#F9FAFB',
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout; 